import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardActionArea, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
  List, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Stack, Switch, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';
import CheckIcon from '@mui/icons-material/Check';
import UndoIcon from '@mui/icons-material/Undo';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { useNavigate } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { CardGrid, EmptyState, Loader, NeedsHousehold, PageHeader, useLoad, useNotify, useRun, useUndoable } from '../common';
import { addDays, dayLabel, freeSlotsAhead, mealText, servings, slotRequest, today, weekStart } from '../labels';
import { PLAN_PATH } from '../meals/shared';
import { FreezerSelect, FrozenBatchDialog, useFreezers } from './FrozenBatchDialog';

const batchTitle = (b: T.FrozenBatch) => `${b.recipeName} · ${servings(b.remaining)}`;

/** "congelado Segunda-feira, 3 nov · do jantar de sábado · 2 doses já planeadas · caixa azul". */
function batchSubtitle(b: T.FrozenBatch): string {
  const parts = [b.frozenOn > today() ? `a congelar ${dayLabel(b.frozenOn)}` : `congelado ${dayLabel(b.frozenOn)}`];
  if (b.fromMeal) parts.push(`do ${b.fromMeal}`);
  if (b.used > 0) parts.push(`${servings(b.used)} já planeadas`);
  // The zone inside the freezer place (the place heads the group)
  if (b.locationName?.includes(' › ')) parts.push(b.locationName.split(' › ')[1]);
  if (b.note) parts.push(b.note);
  return parts.join(' · ');
}

/** Batches by the freezer place they are in (zones under their place), in the order they come. */
function byPlace(list: T.FrozenBatch[]): { place: string; batches: T.FrozenBatch[] }[] {
  const groups: { place: string; batches: T.FrozenBatch[] }[] = [];
  list.forEach(b => {
    const place = b.locationName ? b.locationName.split(' › ')[0] : 'Sem local';
    const group = groups.find(g => g.place === place);
    if (group) group.batches.push(b);
    else groups.push({ place, batches: [b] });
  });
  return groups;
}

/** What is in the freezer, oldest first; tap to change or mark as finished. */
function FreezerContent() {
  const navigate = useNavigate();
  const notify = useNotify();
  const run = useRun();
  const offerUndo = useUndoable();
  const [showFinished, setShowFinished] = useState(false);
  const { data: batches, error, loading, reload } = useLoad(() => api.getFreezer(showFinished), [showFinished]);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; batch: T.FrozenBatch } | null>(null);
  const [editing, setEditing] = useState<{ batch: T.FrozenBatch | null } | null>(null);
  const [noteBatch, setNoteBatch] = useState<T.FrozenBatch | null>(null);
  const [eating, setEating] = useState<T.FrozenBatch | null>(null);

  /** A meal still to plan eats from this batch, with "Desfazer" back to how the meal was. */
  const eat = async (batch: T.FrozenBatch, slot: T.MealSlot) => {
    setEating(null);
    const before = slotRequest(slot);
    const saved = await run(() => api.updateMealSlot(slot.publicId, {
      ...before, kind: 'Frozen', frozenBatchPublicId: batch.publicId, recipePublicId: null, freeText: null,
      leftoversFromSlotPublicId: null, freezeServings: null, sidePublicIds: batch.usualSides.slice(0, 1).map(s => s.publicId),
    }));
    if (saved === undefined) return;
    reload();
    notify(`${batch.recipeName}: ${dayLabel(slot.date).toLowerCase()} ao ${mealText(slot.mealType).toLowerCase()}`, {
      actionText: 'Desfazer',
      action: async () => { await run(() => api.updateMealSlot(slot.publicId, before)); reload(); },
    });
  };

  const setFinished = async (batch: T.FrozenBatch, finished: boolean) => {
    const done = await run(() => api.finishFrozenBatch(batch.publicId, finished));
    if (done === undefined) return;
    await reload();
    if (finished) {
      offerUndo(`${batch.recipeName}: acabou.`, () => api.finishFrozenBatch(batch.publicId, false), reload);
    } else {
      notify(`${batch.recipeName} voltou ao congelador.`, { severity: 'success' });
    }
  };

  const choose = (action: 'eat' | 'edit' | 'note' | 'meal' | 'finished' | 'unfinish') => {
    const batch = menu!.batch;
    setMenu(null);
    switch (action) {
      case 'eat': setEating(batch); break;
      case 'edit': setEditing({ batch }); break;
      case 'note': setNoteBatch(batch); break;
      // Cooked at a planned meal: the portions change in that meal, opened here
      case 'meal':
        navigate(batch.fromSlotPublicId && batch.fromDate
          ? `${PLAN_PATH}/refeicao/${batch.fromSlotPublicId}?semana=${weekStart(batch.fromDate)}`
          : PLAN_PATH);
        break;
      case 'finished': setFinished(batch, true); break;
      case 'unfinish': setFinished(batch, false); break;
    }
  };

  const list = batches ?? [];
  const groups = byPlace(list);
  const current = menu?.batch;

  const batchCard = (b: T.FrozenBatch) => (
    <Card key={b.publicId} sx={{ opacity: b.isFinished ? 0.65 : 1 }}>
      <CardActionArea onClick={e => setMenu({ anchor: e.currentTarget, batch: b })} sx={{ p: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 1.5 }}>
        <Box sx={{ width: 44, height: 44, borderRadius: 2.5, flexShrink: 0, display: 'grid', placeItems: 'center', bgcolor: 'rgba(2, 136, 209, 0.10)', color: 'info.dark', fontWeight: 700, fontSize: 18 }}>
          {b.remaining}
        </Box>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 650, color: b.isFinished ? 'text.disabled' : undefined }}>{batchTitle(b)}</Typography>
            {b.isFinished && <Chip size="small" label="acabou" />}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{batchSubtitle(b)}</Typography>
        </Box>
      </CardActionArea>
    </Card>
  );

  return (
    <>
      <PageHeader
        eyebrow="Comida"
        title="Congelador"
        subtitle="O mais antigo primeiro"
        actions={<>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({ batch: null })}>Juntar</Button>
          <IconButton aria-label="Atualizar" onClick={reload}><RefreshIcon /></IconButton>
        </>}
      />
      <FormControlLabel
        control={<Switch checked={showFinished} onChange={e => setShowFinished(e.target.checked)} />}
        label="Mostrar os que acabaram"
        sx={{ mb: 1 }}
      />

      <Loader loading={loading} error={error} onRetry={reload} hasData={batches !== undefined}>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {groups.map(group => (
          <Box key={group.place} sx={{ mb: 3 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
              <AcUnitIcon fontSize="inherit" /> {group.place} · {group.batches.length}
            </Typography>
            <CardGrid min={280}>
              {group.batches.map(batchCard)}
            </CardGrid>
          </Box>
        ))}
        {list.length === 0 && (
          <Paper variant="outlined">
            <EmptyState
              icon={<AcUnitIcon />}
              title="O congelador está vazio"
              text={'Para congelar ao cozinhar, abre a refeição no Plano e indica as doses a mais. Para comer do congelador, escolhe "Do congelador" na refeição. Também podes juntar à mão.'}
            />
          </Paper>
        )}
      </Loader>

      <Menu anchorEl={menu?.anchor} open={menu !== null} onClose={() => setMenu(null)}>
        {current && (
          <MenuItem disabled sx={{ opacity: '1 !important' }}>
            <ListItemText primary={current.recipeName} secondary={batchSubtitle(current)} slotProps={{ secondary: { sx: { whiteSpace: 'normal', maxWidth: 280 } } }} />
          </MenuItem>
        )}
        {current && !current.isFinished && current.remaining > current.used && (
          <MenuItem onClick={() => choose('eat')}>
            <ListItemIcon><RestaurantIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText primary="Comer numa refeição" secondary="Escolhe uma refeição por planear" />
          </MenuItem>
        )}
        {current && !current.fromMeal && (
          <MenuItem onClick={() => choose('edit')}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Alterar" secondary="Receita, doses, congelador, dia e nota" />
          </MenuItem>
        )}
        {current && current.fromMeal && (
          <MenuItem onClick={() => choose('note')}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Mudar o local e a nota" secondary={[current.locationName, current.note].filter(Boolean).join(' · ') || 'Sem nota'} />
          </MenuItem>
        )}
        {current && current.fromMeal && (
          <MenuItem onClick={() => choose('meal')}>
            <ListItemIcon><EventIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Mudar as doses"
              secondary={`Vem de ${current.fromMeal}: muda-se nessa refeição`}
              slotProps={{ secondary: { sx: { whiteSpace: 'normal', maxWidth: 280 } } }}
            />
          </MenuItem>
        )}
        {current && !current.isFinished && (
          <MenuItem onClick={() => choose('finished')} sx={{ color: 'error.main' }}>
            <ListItemIcon><CheckIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText primary="Acabou / deitei fora" />
          </MenuItem>
        )}
        {current && current.isFinished && (
          <MenuItem onClick={() => choose('unfinish')}>
            <ListItemIcon><UndoIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Ainda há" secondary="Volta a contar no congelador" />
          </MenuItem>
        )}
      </Menu>

      <FrozenBatchDialog
        open={editing !== null}
        batch={editing?.batch ?? null}
        onClose={() => setEditing(null)}
        onSaved={() => {
          notify(editing?.batch ? 'Guardado.' : 'Juntei ao congelador.', { severity: 'success' });
          setEditing(null);
          reload();
        }}
      />
      <EatDialog batch={eating} onClose={() => setEating(null)} onChosen={slot => eating && eat(eating, slot)} />
      <NoteDialog
        batch={noteBatch}
        onClose={() => setNoteBatch(null)}
        onSaved={() => { setNoteBatch(null); reload(); }}
      />
    </>
  );
}

/** "Quando comer …?": the meals still to plan this week and the next. */
function EatDialog({ batch, onClose, onChosen }: {
  batch: T.FrozenBatch | null; onClose: () => void; onChosen: (slot: T.MealSlot) => void;
}) {
  const run = useRun();
  const [free, setFree] = useState<T.MealSlot[] | null>(null);
  useEffect(() => {
    if (!batch) return;
    let active = true;
    setFree(null);
    const thisWeek = weekStart(today());
    run(() => Promise.all([api.getWeekPlan(thisWeek), api.getWeekPlan(addDays(thisWeek, 7))])).then(weeks => {
      if (!active) return;
      if (weeks) setFree(freeSlotsAhead(weeks));
      else onClose();
    });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch]);

  return (
    <Dialog open={batch !== null} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Quando comer {batch?.recipeName}?</DialogTitle>
      <DialogContent dividers>
        {batch && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{servings(batch.remaining)} no congelador</Typography>}
        {free === null && <Typography variant="body2" color="text.secondary">A procurar…</Typography>}
        {free !== null && free.length === 0 && (
          <Typography variant="body2">Não há refeições por planear nestas duas semanas.</Typography>
        )}
        {free !== null && free.length > 0 && (
          <Paper variant="outlined">
            <List dense disablePadding>
              {free.map(s => (
                <ListItemButton key={s.publicId} onClick={() => onChosen(s)}>
                  <ListItemText
                    primary={`${dayLabel(s.date)} · ${mealText(s.mealType).toLowerCase()}`}
                    secondary={s.attendeePublicIds.length > 0 || s.guests > 0 ? undefined : 'ninguém come'}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancelar</Button></DialogActions>
    </Dialog>
  );
}

/** Changes the freezer and the note of a batch cooked at a planned meal (the rest follows that meal). */
function NoteDialog({ batch, onClose, onSaved }: { batch: T.FrozenBatch | null; onClose: () => void; onSaved: () => void }) {
  const run = useRun();
  const [note, setNote] = useState('');
  const [locationId, setLocationId] = useState('');
  const freezers = useFreezers(batch !== null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (batch) setNote(batch.note ?? ''); }, [batch]);
  useEffect(() => {
    if (batch && freezers) setLocationId(batch.locationPublicId ?? freezers.find(l => !l.parentPublicId)?.publicId ?? '');
  }, [batch, freezers]);

  const save = async () => {
    if (!batch) return;
    setBusy(true);
    const result = await run(() => api.updateFrozenBatch(batch.publicId, {
      recipePublicId: batch.recipePublicId, servings: batch.servings, frozenOn: batch.frozenOn, note: note.trim() || null,
      locationPublicId: locationId || null,
    }));
    setBusy(false);
    if (result !== undefined) onSaved();
  };

  return (
    <Dialog open={batch !== null} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{batch?.recipeName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FreezerSelect freezers={freezers} value={locationId} onChange={setLocationId} />
          <TextField
            label="Nota (caixa, prateleira…)"
            value={note}
            onChange={e => setNote(e.target.value)}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 200 } }}
            onKeyDown={e => { if (e.key === 'Enter') save(); }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={busy}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
}

const FreezerPage: React.FC = () => <NeedsHousehold><FreezerContent /></NeedsHousehold>;

export default FreezerPage;
